import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, FileText, Layers, LayoutGrid, List, Palette, Ruler, Scale, Search, Shapes, Tag } from "lucide-react";
import client, { unwrap } from "@/api/client";
import ProductCard, { ProductRow, productColour, productMaterial, productTypeLabel } from "@/components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { catalogueProductsQuery } from "@/lib/sessionQuery";
import { cn } from "@/lib/utils";
import { titleCase } from "@/lib/format";

const ALL = "all";

// Products imported from a PDF carry their design (quality); group shades under it. Others fall
// back to one group per catalogue, as before.
function groupProducts(products) {
  const groups = new Map();
  for (const p of products) {
    const key = p.design ? `design:${p.design.id}` : `${p.companyName || "Catalogue"} — ${p.versionName || p.catalogueReference || ""}`;
    if (!groups.has(key)) groups.set(key, { key, label: p.design ? p.design.name : key, design: p.design || null, products: [] });
    groups.get(key).products.push(p);
  }
  return Array.from(groups.values());
}

function designSpecs(d) {
  return [
    d.designNumber && `Design ${d.designNumber}`,
    d.fabricType,
    d.composition,
    d.widthCm && `${d.widthCm} cm wide`,
    d.weightGsm && `${d.weightGsm} GSM`,
    d.martindaleRubs && `${d.martindaleRubs.toLocaleString("en-IN")} rubs`,
    d.usage?.length && d.usage.join(", "),
  ].filter(Boolean);
}

// Summaries for the header: one value when everything agrees, otherwise a count or a range,
// so a mixed catalogue never shows a single misleading spec.
const summarise = (values, plural) => {
  const unique = [...new Set(values.filter(Boolean))];
  if (unique.length === 0) return null;
  return unique.length === 1 ? unique[0] : `${unique.length} ${plural}`;
};
const range = (values, unit) => {
  const nums = values.filter((v) => typeof v === "number");
  if (nums.length === 0) return null;
  const [min, max] = [Math.min(...nums), Math.max(...nums)];
  return min === max ? `${min} ${unit}` : `${min}–${max} ${unit}`;
};
const uniqueSorted = (values) => [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));

const STATUS_CHIP = {
  APPROVED: "bg-success/10 text-success",
  PENDING_APPROVAL: "bg-warning/15 text-[hsl(41_60%_32%)]",
  CHANGES_REQUESTED: "bg-warning/15 text-[hsl(41_60%_32%)]",
  REJECTED: "bg-danger/10 text-danger",
};

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="flex items-start gap-2.5 min-w-0">
      <Icon className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" strokeWidth={1.5} />
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, options, allLabel, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="w-full sm:w-[170px] rounded-lg bg-card"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{allLabel}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function CatalogueDetailPage() {
  const { catalogueId } = useParams();
  const [designTab, setDesignTab] = useState(ALL);
  const [search, setSearch] = useState("");
  const [colour, setColour] = useState(ALL);
  const [type, setType] = useState(ALL);
  const [view, setView] = useState("grid");

  const { data: products = [], isLoading } = useQuery(catalogueProductsQuery(catalogueId));
  // Status and description live on the catalogue record; the list is usually cached from the Catalogues page.
  const { data: catalogues = [] } = useQuery({ queryKey: ["catalogues"], queryFn: () => client.get("/catalogues").then(unwrap) });
  const catalogue = catalogues.find((c) => c._id === catalogueId);

  const groups = useMemo(() => groupProducts(products), [products]);
  const designGroups = groups.filter((g) => g.design);
  // Every product carries its catalogue's name and version, so the header needs no extra request.
  const { companyName, versionName, catalogueReference } = products[0] || catalogue || {};
  const title = versionName || catalogueReference;

  const designs = designGroups.map((g) => g.design);
  // Supplier PDFs reuse a design name across several design numbers (e.g. four "Gara"s), so tabs go by name.
  const designTabs = Object.values(
    designGroups.reduce((m, g) => {
      const t = (m[g.label] ||= { key: g.label, label: g.label, count: 0 });
      t.count += g.products.length;
      return m;
    }, {}),
  );
  const stats = [
    { icon: FileText, value: products.length, label: products.length === 1 ? "Product" : "Products" },
    designs.length > 0 && { icon: Layers, value: designs.length, label: designs.length === 1 ? "Design" : "Designs" },
    designs.length > 0 && { icon: Palette, value: Math.round(products.length / designs.length), label: "Avg. shades" },
    summarise(products.map(productMaterial), "fabrics") && { icon: Shapes, value: summarise(products.map(productMaterial), "fabrics"), label: "Fabric" },
    range(designs.map((d) => d.widthCm), "cm") && { icon: Ruler, value: range(designs.map((d) => d.widthCm), "cm"), label: "Width" },
    range(designs.map((d) => d.weightGsm), "GSM") && { icon: Scale, value: range(designs.map((d) => d.weightGsm), "GSM"), label: "Weight" },
  ].filter(Boolean);
  const productTypes = uniqueSorted(products.map(productTypeLabel));
  const heroImage = designs.find((d) => d.heroImageUrl)?.heroImageUrl || products.find((p) => p.imageUrls?.length)?.imageUrls[0];

  const colourOptions = uniqueSorted(products.map(productColour));
  const filtersActive = search || colour !== ALL || type !== ALL || designTab !== ALL;
  const clearFilters = () => {
    setSearch("");
    setColour(ALL);
    setType(ALL);
    setDesignTab(ALL);
  };

  const visibleGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    return groups
      .filter((g) => designTab === ALL || g.label === designTab)
      .map((g) => ({
        ...g,
        products: g.products.filter(
          (p) =>
            (colour === ALL || productColour(p) === colour) &&
            (type === ALL || productTypeLabel(p) === type) &&
            (!term || [p.name, p.shopReference, p.shadeCode, productColour(p)].some((v) => v?.toLowerCase().includes(term))),
        ),
      }))
      .filter((g) => g.products.length > 0);
  }, [groups, designTab, colour, type, search]);

  return (
    <div data-testid="catalogue-detail-page" className="space-y-6">
      <header className="relative overflow-hidden rounded-2xl bg-sidebar/60 border border-border">
        {heroImage && (
          <div className="hidden lg:block absolute inset-y-0 right-0 w-[38%]" aria-hidden="true">
            <img src={heroImage} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--sidebar))] via-[hsl(var(--sidebar)/0.4)] to-transparent" />
          </div>
        )}
        <div className="relative px-6 sm:px-8 py-6 lg:max-w-[66%]">
          <nav className="flex items-center gap-1.5 text-sm text-muted-foreground" aria-label="Breadcrumb">
            <Link to="/catalogues" className="hover:text-foreground">Catalogues</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-foreground truncate">{[companyName, title].filter(Boolean).join(" – ") || "Catalogue"}</span>
          </nav>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <h1 className="font-serif text-3xl sm:text-4xl leading-tight text-foreground" data-testid="catalogue-detail-title">
              {companyName || "Catalogue"}
              {title && <span className="text-muted-foreground"> · {title}</span>}
            </h1>
            {catalogue?.status && (
              <span className={cn("rounded-full px-3 py-1 text-[11px] font-semibold tracking-wider uppercase", STATUS_CHIP[catalogue.status] || "bg-secondary text-muted-foreground")}>
                {titleCase(catalogue.status)}
              </span>
            )}
          </div>
          <p className="text-muted-foreground mt-2">
            {catalogue?.description || "Each product shows the shop-specific reference generated uniquely for your tenant."}
          </p>
          {products.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-x-8 gap-y-4" data-testid="catalogue-stats">
              {stats.map((s) => <Stat key={s.label} {...s} />)}
            </div>
          )}
          {productTypes.length > 0 && (
            <div className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs text-foreground/80">
              <Tag className="h-3.5 w-3.5 text-muted-foreground" /> {productTypes.join(", ")}
            </div>
          )}
        </div>
      </header>

      {designTabs.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" role="tablist" data-testid="catalogue-design-tabs">
          {[{ key: ALL, label: "All products", count: products.length }, ...designTabs].map((t) => {
            const active = designTab === t.key;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={active}
                onClick={() => setDesignTab(t.key)}
                className={cn(
                  "shrink-0 inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-medium transition-colors",
                  active ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground hover:bg-border",
                )}
              >
                {t.label}
                <span className={cn("text-xs tabular-nums", active ? "text-primary-foreground/70" : "text-muted-foreground")}>{t.count}</span>
              </button>
            );
          })}
        </div>
      )}

      <section className="bg-card border border-border rounded-2xl p-4 flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative flex-1 lg:max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            data-testid="catalogue-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search within this catalogue…"
            className="w-full rounded-full border border-border bg-secondary/70 pl-10 pr-4 py-2.5 text-sm placeholder:text-muted-foreground focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {colourOptions.length > 1 && <FilterSelect testId="catalogue-colour-filter" value={colour} onChange={setColour} options={colourOptions} allLabel="All colours" />}
          {productTypes.length > 1 && <FilterSelect testId="catalogue-type-filter" value={type} onChange={setType} options={productTypes} allLabel="All types" />}
        </div>
        {filtersActive && (
          <button data-testid="catalogue-clear-filters" onClick={clearFilters} className="text-sm font-medium text-accent hover:underline text-left lg:ml-2">
            Clear filters
          </button>
        )}
        <div className="hidden md:flex items-center gap-1 lg:ml-auto rounded-lg border border-border p-0.5" role="group" aria-label="View">
          {[["grid", LayoutGrid], ["list", List]].map(([key, Icon]) => (
            <button
              key={key}
              aria-label={`${key} view`}
              aria-pressed={view === key}
              data-testid={`catalogue-view-${key}`}
              onClick={() => setView(key)}
              className={cn("p-2 rounded-md transition-colors", view === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary")}
            >
              <Icon className="h-4 w-4" />
            </button>
          ))}
        </div>
      </section>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {visibleGroups.map((group) => (
        <section key={group.key} className="space-y-4" data-testid="catalogue-product-group">
          {group.design ? (
            <div className="flex items-start gap-4">
              {group.design.heroImageUrl && (
                <img src={group.design.heroImageUrl} alt={group.design.name} className="hidden sm:block h-14 w-14 rounded-xl object-cover border border-border shrink-0" />
              )}
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="font-serif text-2xl sm:text-3xl text-foreground">
                    {group.design.name}
                    {group.design.designNumber && <span className="text-muted-foreground text-lg sm:text-xl"> #{group.design.designNumber}</span>}
                  </h2>
                  <span className="rounded-full bg-[hsl(30_60%_90%)] px-3 py-1 text-xs font-medium text-[hsl(25_45%_35%)]">
                    {group.products.length} shade{group.products.length === 1 ? "" : "s"}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {designSpecs({ ...group.design, designNumber: null }).join(" · ")}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-[11px] font-semibold tracking-widest text-muted-foreground uppercase">{group.label}</p>
          )}
          {view === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {group.products.map((p) => <ProductCard key={p.id} p={p} showCatalogue={false} />)}
            </div>
          ) : (
            <ul className="bg-card border border-border rounded-2xl overflow-hidden divide-y divide-border">
              {group.products.map((p) => <ProductRow key={p.id} p={p} showCatalogue={false} />)}
            </ul>
          )}
        </section>
      ))}

      {!isLoading && products.length === 0 && <p className="text-muted-foreground text-sm">No products in this catalogue.</p>}
      {!isLoading && products.length > 0 && visibleGroups.length === 0 && (
        <div className="bg-card border border-dashed border-border rounded-2xl py-14 text-center">
          <p className="text-sm text-muted-foreground">No products match these filters.</p>
          <button onClick={clearFilters} className="mt-2 text-sm font-medium text-accent hover:underline">Clear filters</button>
        </div>
      )}
    </div>
  );
}
